import { HOUR, MINUTE } from './types.js';
/** The account-level exhaustion marker, if still in force at `now`. */
export function activeLimit(snapshot, now, config = {}) {
    const l = snapshot?.limitReached;
    if (!l)
        return undefined;
    const until = l.resetsAt ?? l.at + (config.unknownResetParkMs ?? DEFAULT_USAGE_CONFIG.unknownResetParkMs);
    if (now >= until)
        return undefined;
    return { until, stated: l.resetsAt !== undefined, ...(l.code ? { code: l.code } : {}) };
}
export const DEFAULT_USAGE_CONFIG = {
    parkAtPct: 90,
    paceAtPct: 75,
    staleAfterMs: 15 * MINUTE,
    paceMaxConcurrent: 2,
    staleMaxConcurrent: 2,
    unknownMaxConcurrent: 3,
    unknownResetParkMs: 30 * MINUTE,
    warningAssumedPct: 80,
    ignoreWindows: ['overage'],
    minBurnSpanMs: 5 * MINUTE,
    maxSamples: 24,
};
const NAMED_MINUTES = {
    '5h': 300,
    '7d': 10080,
    '7d_opus': 10080,
    '7d_sonnet': 10080,
};
/** Key a window by its name, or by its duration for `other` (Codex windows vary: key off minutes). */
export function windowKey(w) {
    if (w.window === 'other')
        return `other:${w.windowMinutes ?? 'unknown'}`;
    return w.window;
}
function windowMinutesOf(w) {
    return w.windowMinutes ?? NAMED_MINUTES[w.window] ?? 300;
}
/**
 * Merge a rate-limit observation into the account snapshot. Windows not mentioned keep their
 * previous observation (Claude emits one window per `rate_limit_event`). Samples reset when the
 * window rolls over (new `resetsAt`, or utilization drops).
 */
export function ingestRateLimit(prev, input, config = {}) {
    const cfg = { ...DEFAULT_USAGE_CONFIG, ...config };
    const byKey = new Map();
    for (const w of prev?.windows ?? [])
        byKey.set(w.key, w);
    for (const w of input.windows) {
        const key = windowKey(w);
        const old = byKey.get(key);
        // Out-of-order observation: keep the newer one.
        if (old && old.observedAt > input.observedAt)
            continue;
        const rolled = !!old &&
            ((w.resetsAt !== undefined &&
                old.resetsAt !== undefined &&
                w.resetsAt > old.resetsAt + MINUTE) ||
                (w.usedPct !== undefined && old.usedPct !== undefined && w.usedPct + 1 < old.usedPct) ||
                (old.resetsAt !== undefined && input.observedAt >= old.resetsAt));
        let samples = rolled || !old ? [] : [...old.samples];
        if (w.usedPct !== undefined) {
            samples.push({ at: input.observedAt, usedPct: w.usedPct });
            if (samples.length > cfg.maxSamples)
                samples = samples.slice(-cfg.maxSamples);
        }
        const next = {
            key,
            name: w.window,
            windowMinutes: windowMinutesOf(w),
            observedAt: input.observedAt,
            samples,
        };
        const nativeName = w.nativeName ?? old?.nativeName;
        if (nativeName !== undefined)
            next.nativeName = nativeName;
        const usedPct = w.usedPct ?? (rolled ? undefined : old?.usedPct);
        if (usedPct !== undefined)
            next.usedPct = usedPct;
        const resetsAt = w.resetsAt ?? (rolled ? undefined : old?.resetsAt);
        if (resetsAt !== undefined)
            next.resetsAt = resetsAt;
        if (w.status !== undefined)
            next.status = w.status;
        byKey.set(key, next);
    }
    const windows = [...byKey.values()].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const snap = {
        accountId: input.accountId,
        agent: input.agent,
        observedAt: Math.max(prev?.observedAt ?? 0, input.observedAt),
        windows,
    };
    const credits = input.credits && input.observedAt >= (prev?.credits?.observedAt ?? 0)
        ? { ...input.credits, observedAt: input.observedAt }
        : prev?.credits;
    if (credits)
        snap.credits = credits;
    if (input.limitReached && input.observedAt >= (prev?.limitReached?.at ?? 0)) {
        snap.limitReached = { at: input.observedAt, ...input.limitReached };
    }
    else if (prev?.limitReached && activeLimit(prev, snap.observedAt, cfg)) {
        snap.limitReached = prev.limitReached;
    }
    const plan = input.plan ?? prev?.plan;
    if (plan !== undefined)
        snap.plan = plan;
    return snap;
}
/** Convenience: ingest a normalized `ratelimit` event (agent + time come from the envelope). */
export function ingestRateLimitEvent(snapshots, accountId, event, config = {}) {
    const input = {
        accountId,
        agent: event.agent,
        observedAt: event.ts,
        windows: event.windows,
    };
    if (event.credits !== undefined)
        input.credits = event.credits;
    if (event.plan !== undefined)
        input.plan = event.plan;
    if (event.limitReached !== undefined)
        input.limitReached = event.limitReached;
    return { ...snapshots, [accountId]: ingestRateLimit(snapshots[accountId], input, config) };
}
function burnRate(win, minSpan) {
    const s = win.samples;
    if (s.length < 2)
        return undefined;
    const first = s[0];
    const last = s[s.length - 1];
    const span = last.at - first.at;
    if (span < minSpan)
        return undefined;
    return Math.max(0, ((last.usedPct - first.usedPct) / span) * HOUR);
}
/** Project a window forward to `now`: reset-aware, burn-rate based, conservative when stale. */
export function projectWindow(win, now, config = {}) {
    const cfg = { ...DEFAULT_USAGE_CONFIG, ...config };
    const ageMs = Math.max(0, now - win.observedAt);
    const stale = ageMs > cfg.staleAfterMs;
    const fairRate = 100 / (win.windowMinutes / 60); // %/h if the window were spent evenly
    const rate = burnRate(win, cfg.minBurnSpanMs);
    const out = { key: win.key, stale, ageMs, rejected: false };
    if (rate !== undefined)
        out.burnPctPerHour = rate;
    let used = win.usedPct;
    if (used === undefined && win.status === 'rejected')
        used = 100;
    if (used === undefined && win.status === 'warning')
        used = cfg.warningAssumedPct;
    let resetsAt = win.resetsAt;
    const periodMs = win.windowMinutes * MINUTE;
    if (resetsAt !== undefined && now >= resetsAt) {
        // The observed window has rolled over: start from zero at the reset, assume a burn since.
        const periods = Math.floor((now - resetsAt) / periodMs) + 1;
        const lastReset = resetsAt + (periods - 1) * periodMs;
        resetsAt = lastReset + periodMs;
        const assumed = Math.max(rate ?? 0, fairRate);
        used = Math.min(100, ((now - lastReset) / HOUR) * assumed);
    }
    else {
        if (win.status === 'rejected')
            out.rejected = true;
        if (used !== undefined && stale) {
            const assumed = Math.max(rate ?? 0, fairRate);
            used = Math.min(100, used + ((ageMs - cfg.staleAfterMs) / HOUR) * assumed);
        }
    }
    if (used !== undefined)
        out.effectiveUsedPct = used;
    if (resetsAt !== undefined)
        out.resetsAt = resetsAt;
    if (used !== undefined && rate !== undefined && resetsAt !== undefined) {
        out.projectedPctAtReset = used + ((resetsAt - now) / HOUR) * rate;
        if (used < cfg.parkAtPct && rate > 0) {
            const at = now + ((cfg.parkAtPct - used) / rate) * HOUR;
            if (at < resetsAt)
                out.reachesParkAt = Math.round(at);
        }
    }
    return out;
}
const pct = (n) => `${Math.round(n)}%`;
const iso = (t) => new Date(t).toISOString();
/**
 * Headroom + pacing decision for an account:
 * - park when any window is rejected or at/over `parkAtPct` — until that window's `resetsAt`
 * - pace (cap concurrency) at `paceAtPct`, or when the burn rate reaches `parkAtPct` before reset
 * - conservative cap when every window is stale, or nothing is known
 */
export function accountBudget(snapshot, account, now, config = {}) {
    const cfg = { ...DEFAULT_USAGE_CONFIG, ...config };
    const base = { accountId: account.accountId, agent: account.agent };
    const limit = activeLimit(snapshot, now, cfg);
    if (limit) {
        const windows = (snapshot?.windows ?? [])
            .filter((w) => !cfg.ignoreWindows.includes(w.name))
            .map((w) => projectWindow(w, now, cfg));
        return {
            ...base,
            state: 'park',
            freshness: windows.length && windows.every((w) => w.stale) ? 'stale' : 'fresh',
            headroomPct: 0,
            maxConcurrent: 0,
            parkUntil: limit.until,
            bindingWindow: 'limit',
            windows,
            reasons: [
                `usage limit reached; park until ${iso(limit.until)}${limit.stated ? '' : ' (no reset stated; recheck then)'}`,
            ],
        };
    }
    const relevant = (snapshot?.windows ?? []).filter((w) => !cfg.ignoreWindows.includes(w.name));
    if (relevant.length === 0) {
        return {
            ...base,
            state: 'unknown',
            freshness: 'unknown',
            headroomPct: null,
            maxConcurrent: cfg.unknownMaxConcurrent,
            windows: [],
            reasons: [
                `no usage data for ${account.accountId}; conservative cap ${cfg.unknownMaxConcurrent}`,
            ],
        };
    }
    const windows = relevant.map((w) => projectWindow(w, now, cfg));
    const known = windows.filter((w) => w.effectiveUsedPct !== undefined);
    const maxUsed = known.reduce((m, w) => Math.max(m, w.effectiveUsedPct), 0);
    const allStale = windows.every((w) => w.stale);
    const freshness = allStale ? 'stale' : 'fresh';
    const headroomPct = known.length ? cfg.parkAtPct - maxUsed : null;
    const reasons = [];
    // Park: rejected or over threshold. Wait for the latest reset among the binding windows.
    const over = windows.filter((w) => w.rejected || (w.effectiveUsedPct !== undefined && w.effectiveUsedPct >= cfg.parkAtPct));
    if (over.length) {
        let parkUntil = 0;
        let binding = over[0];
        for (const w of over) {
            const until = w.resetsAt ?? now + cfg.unknownResetParkMs;
            if (until > parkUntil) {
                parkUntil = until;
                binding = w;
            }
            reasons.push(w.rejected
                ? `${w.key} window rejected; park until ${iso(until)}`
                : `${w.key} at ${pct(w.effectiveUsedPct)} ≥ ${cfg.parkAtPct}%; park until ${iso(until)}`);
        }
        return {
            ...base,
            state: 'park',
            freshness,
            headroomPct,
            maxConcurrent: 0,
            parkUntil,
            bindingWindow: binding.key,
            windows,
            reasons,
        };
    }
    let state = 'ok';
    let maxConcurrent = null;
    let bindingWindow;
    const cap = (n) => {
        maxConcurrent = maxConcurrent === null ? n : Math.min(maxConcurrent, n);
    };
    for (const w of windows) {
        if (w.effectiveUsedPct !== undefined && w.effectiveUsedPct >= cfg.paceAtPct) {
            state = 'pace';
            cap(cfg.paceMaxConcurrent);
            bindingWindow ??= w.key;
            reasons.push(`${w.key} at ${pct(w.effectiveUsedPct)} ≥ ${cfg.paceAtPct}%; pacing`);
        }
        else if (w.reachesParkAt !== undefined) {
            state = 'pace';
            cap(cfg.paceMaxConcurrent);
            bindingWindow ??= w.key;
            reasons.push(`${w.key} burning ${pct(w.burnPctPerHour ?? 0)}/h, reaches ${cfg.parkAtPct}% at ${iso(w.reachesParkAt)} before reset; pacing`);
        }
    }
    if (allStale) {
        if (state === 'ok')
            state = 'pace';
        cap(cfg.staleMaxConcurrent);
        const age = Math.min(...windows.map((w) => w.ageMs));
        reasons.push(`usage data stale (${Math.round(age / MINUTE)} min old); conservative cap ${cfg.staleMaxConcurrent}`);
    }
    if (state === 'ok') {
        reasons.push(headroomPct === null ? 'usage ok' : `headroom ${pct(headroomPct)} to ${cfg.parkAtPct}%`);
    }
    const out = {
        ...base,
        state,
        freshness,
        headroomPct,
        maxConcurrent,
        windows,
        reasons,
    };
    if (bindingWindow !== undefined)
        out.bindingWindow = bindingWindow;
    return out;
}
//# sourceMappingURL=usage.js.map